"use client";

import { Children, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";

gsap.registerPlugin(useGSAP, ScrollTrigger);

type RevealProps = {
  children: React.ReactNode;
  className?: string;
  /** Retardo entre hijos consecutivos */
  stagger?: number;
  /** Desplazamiento vertical inicial, en px */
  y?: number;
};

/**
 * Anima la entrada de los hijos directos cuando el grupo entra en pantalla.
 *
 * `useGSAP` corre en useLayoutEffect, así que el estado inicial se aplica antes
 * del primer pintado y no hay parpadeo entre el HTML del servidor y la animación.
 * Bajo `prefers-reduced-motion` no se anima nada y los hijos quedan visibles.
 */
export default function Reveal({
  children,
  className,
  stagger = 0.08,
  y = 24,
}: RevealProps) {
  const ref = useRef<HTMLDivElement>(null);
  // Depender de `children` reventaba la animación: es una referencia nueva en
  // cada render, así que el efecto se revertía y recreaba sin parar y el
  // ScrollTrigger nunca llegaba a dispararse. El número de hijos sí es estable
  // y cambia justo cuando hace falta rehacerla (skeleton -> datos).
  const childCount = Children.count(children);

  useGSAP(
    () => {
      const targets = ref.current ? Array.from(ref.current.children) : [];
      if (!targets.length) return;
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

      // `fromTo` y no `from`: con `from`, el estado final es «lo que el elemento
      // tenga ahora». Si el efecto se remonta (StrictMode) mientras los hijos
      // están en opacidad 0, el nuevo tween animaría de 0 a 0 y quedarían
      // invisibles para siempre. Con el destino explícito eso no puede pasar.
      const tween = gsap.fromTo(
        targets,
        { y, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.6,
          ease: "power2.out",
          stagger,
          scrollTrigger: { trigger: ref.current, start: "top 85%", once: true },
        },
      );

      // Los datos llegan por fetch y las imágenes cambian la altura de la
      // página después de que ScrollTrigger calculó sus posiciones.
      ScrollTrigger.refresh();

      return () => {
        tween.scrollTrigger?.kill();
        tween.revert();
      };
    },
    { scope: ref, dependencies: [childCount, stagger, y] },
  );

  return (
    <div ref={ref} className={className}>
      {children}
    </div>
  );
}
